import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Card from './Card';
import DataItem from './DataItem';
import {
  faCalendarDay,
  faCheckToSlot,
  faCircleXmark,
  faHourglassClock,
  faLayerGroup
} from '@fortawesome/pro-duotone-svg-icons';
import Button from 'components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faCircleCheck
} from '@fortawesome/pro-solid-svg-icons';
import { fetchQuery, graphql } from 'relay-runtime';
import environment from 'api/environment';
import { SimulatorCardQueryResponse } from './__generated__/SimulatorCardQuery.graphql';
import moment from 'moment';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import SimulatorImagesReviewModal from 'features/simulator/components/SimulatorImagesReviewModal';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    width: 420
  },
  overview: {
    display: 'flex',
    gap: '0.5rem'
  },
  monthOverview: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  monthlyData: {
    marginTop: '2rem'
  },
  monthSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  monthText: {
    fontSize: '0.9rem'
  },
  smallText: {
    fontSize: '0.7rem',
    color: '#6A728F'
  },
  title: {
    margin: 0,
    padding: 0,
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '1.4rem',
    marginTop: '0.2rem'
  },
  showImages: {},
  monthlyData__header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.0rem'
  }
}));

const query = graphql`
  query SimulatorCardQuery($uuid: UUID!, $startDate: Time!, $endDate: Time!) {
    delegate(uuid: $uuid) {
      simulator {
        compliance(startDate: $startDate, endDate: $endDate) {
          compliant
          minsCompleted
          requiredMins
          startDate
          successRate
          imagesSeen
        }
      }
    }
  }
`;

type Props = {
  delegateUUID: string;
};

function SimulatorCard(props: Props) {
  const classes = useStyles();

  const [filter, setFilter] = React.useState({
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month')
  });

  const [delegate, setDelegate] = React.useState<
    SimulatorCardQueryResponse['delegate']
  >(null);

  const [simulatorOpen, setSimulatorOpen] = React.useState(false);

  React.useEffect(() => {
    const variables = {
      uuid: props.delegateUUID,
      startDate: filter.startDate.toISOString(),
      endDate: filter.endDate.toISOString()
    };

    fetchQuery(environment, query, variables).then(
      (data: SimulatorCardQueryResponse) => {
        setDelegate(data.delegate);
      }
    );
  }, [filter, props.delegateUUID]);

  return (
    <Card title="6x6 training" padding={'0.8rem'} className={classes.root}>
      <div className={classes.overview}>
        <DataItem
          icon={
            delegate?.simulator?.compliance.compliant
              ? faCircleCheck
              : faCircleXmark
          }
          label="Compliance"
          iconColor={
            delegate?.simulator?.compliance.compliant ? '#18DD20' : '#D75C41'
          }
          style={{ width: '12rem' }}
          helpText="Has the user completed the required 6 hours in 6 months (pro rata)"
        >
          {delegate?.simulator?.compliance.compliant
            ? 'Compliant'
            : 'Non-compliant'}
        </DataItem>
        <DataItem
          icon={faCalendarDay}
          label="Start date"
          iconColor={'#6A728F'}
          style={{ width: '12rem' }}
          helpText="The date the user started 6x6 training with TTC"
        >
          {moment(delegate?.simulator?.compliance.startDate).format(
            'DD MMM YYYY'
          )}
        </DataItem>
      </div>
      <div className={classes.monthlyData}>
        <h4 className={classes.title}>Monthly data</h4>
        <div className={classes.monthlyData__header}>
          <div className={classes.monthSelector}>
            <Button
              archetype="flat"
              small
              onClick={() => {
                if (
                  filter.startDate.isSameOrBefore(
                    moment(delegate?.simulator?.compliance.startDate).startOf(
                      'month'
                    )
                  )
                ) {
                  return;
                }
                setFilter({
                  // Create a new moment object to avoid mutating the original
                  startDate: moment(filter.startDate).subtract(1, 'month'),
                  endDate: moment(filter.endDate).subtract(1, 'month')
                });
              }}
              disabled={filter.startDate.isSameOrBefore(
                moment(delegate?.simulator?.compliance.startDate).startOf(
                  'month'
                )
              )}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <div className={classes.monthText}>
              {filter.startDate.format('MMM YYYY')}
            </div>
            <Button
              archetype="flat"
              small
              onClick={() => {
                if (filter.endDate.isSameOrAfter(moment().endOf('month'))) {
                  return;
                }
                setFilter({
                  // Create a new moment object to avoid mutating the original
                  startDate: moment(filter.startDate).add(1, 'month'),
                  endDate: moment(filter.endDate).add(1, 'month')
                });
              }}
              disabled={filter.endDate.isSameOrAfter(moment().endOf('month'))}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
          <Button
            archetype="flat"
            className={classes.showImages}
            onClick={() => {
              setSimulatorOpen(true);
            }}
          >
            Show images
          </Button>
        </div>
        <Spacer vertical spacing={1} />
        <div className={classes.monthOverview}>
          <DataItem
            icon={faHourglassClock}
            label="Time spent"
            iconColor={
              (delegate?.simulator?.compliance.minsCompleted ?? 0) < 60
                ? '#D75C41'
                : '#6A728F'
            }
            style={{ width: '12rem' }}
            helpText="Time spent training this month"
          >
            {delegate?.simulator?.compliance.minsCompleted}{' '}
            <span className={classes.smallText}>/60 mins</span>
          </DataItem>
          <DataItem
            icon={faLayerGroup}
            label="Images seen"
            style={{ width: '12rem' }}
            iconColor={'#6A728F'}
            onClick={() => {
              setSimulatorOpen(true);
            }}
            helpText="Number of images seen this month"
          >
            {delegate?.simulator?.compliance.imagesSeen}
          </DataItem>
          <DataItem
            icon={faCheckToSlot}
            label="Success rate"
            style={{ width: '12rem' }}
            iconColor={'#6A728F'}
            onClick={() => {
              setSimulatorOpen(true);
            }}
            helpText="Percent of images correctly identified"
          >
            {Math.round(delegate?.simulator?.compliance.successRate ?? 0)}%
          </DataItem>
        </div>
        {simulatorOpen && (
          <SimulatorImagesReviewModal
            month={filter.startDate}
            delegateUUID={props.delegateUUID}
            onClose={() => setSimulatorOpen(false)}
          />
        )}
      </div>
    </Card>
  );
}

export default SimulatorCard;
